import React from 'react'
import { createPortal } from 'react-dom'
import { useIsSSR } from '../../utils/use-is-ssr'

const portalContext = React.createContext<(() => HTMLElement | null) | null>(null)

export function PortalProvider({
  getContainer,
  children
}: {
  getContainer: (() => HTMLElement | null) | null
  children?: React.ReactNode
}) {
  return <portalContext.Provider value={getContainer}>{children}</portalContext.Provider>
}

export function Portal({ children }: { children?: React.ReactNode }) {
  const getContainer = React.useContext(portalContext)
  const isSSR = useIsSSR()

  // Portals are unsupported by the server renderer, and `document` is undefined during SSR.
  // Render children into a <template> (never displayed, kept out of the DOM/a11y tree) on the
  // server; switch to the real portal on the client. useIsSSR flips to false right after
  // hydration, so the SSR markup matches the client's first render — no hydration mismatch.
  return isSSR ? (
    <template data-react-aria-hidden>{children}</template>
  ) : (
    createPortal(children, getContainer ? (getContainer() ?? document.body) : document.body)
  )
}
